import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Save, Eye, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, money } from "@/lib/api";
import { ImageUploader, GalleryUploader } from "@/components/admin/ImageUploader";

const uid = () => Math.random().toString(36).slice(2, 10);

const EMPTY = {
  title: "Untitled Proposal", prepared_by: "", proposal_number: "", customer_id: "", customer: {},
  project_name: "", project_address: "", project_description: "", overview: "",
  cover_image: "", logo: "", images: [], scope_of_work: [], materials_specs: [],
  line_items: [], discount_type: "percent", discount_value: 0, tax_rate: 0,
  scope_includes: [], exclusions: [], quantities: [], our_work: [], terms_items: [],
  labor_total: 0, mobilizations_included: "", estimated_duration: "", crew_size: "", show_signature: false,
  additional_costs: [], notes: "", terms: "", payment_schedule: [], timeline: [],
  warranty: "", before_image: "", after_image: "", status: "draft", date: "", expiration_date: "",
};

const input = "w-full bg-background border border-border px-3 py-2 text-sm outline-none focus:border-foreground transition-colors";
const labelCls = "text-[11px] tracking-[0.12em] uppercase text-muted-foreground block mb-1.5";

const Card = ({ title, children, action }) => (
  <div className="border border-border bg-card">
    <div className="flex items-center justify-between px-5 py-3 border-b border-border">
      <h3 className="font-display font-bold tracking-tight">{title}</h3>
      {action}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

// Editable list of plain strings (scope, materials).
const StringList = ({ items, onChange, placeholder, testid }) => (
  <div className="space-y-2">
    {items.map((v, i) => (
      <div key={i} className="flex gap-2">
        <input className={input} value={v} placeholder={placeholder} onChange={(e) => onChange(items.map((x, idx) => (idx === i ? e.target.value : x)))} />
        <button onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-muted-foreground hover:text-destructive px-2"><Trash2 size={15} /></button>
      </div>
    ))}
    <button onClick={() => onChange([...items, ""])} data-testid={testid} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5"><Plus size={14} /> Add</button>
  </div>
);

export default function ProposalBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const [f, setF] = useState(EMPTY);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    if (isNew) {
      api.get("/settings").then((r) => {
        const s = r.data;
        setF((p) => ({ ...p, terms: s.default_terms || "", warranty: s.default_warranty || "", logo: s.logo || "" }));
      }).catch(() => {});
    } else {
      api.get(`/proposals/${id}`).then((r) => setF({ ...EMPTY, ...r.data })).catch(() => toast.error("Not found")).finally(() => setLoading(false));
    }
  }, [id, isNew]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  // live totals (lump-sum Investment model: base = Labor / Installation)
  const base = Number(f.labor_total) || 0;
  const additionalTotal = (f.additional_costs || []).reduce((a, c) => a + (Number(c.amount) || 0), 0);
  const total = base + additionalTotal;

  const selectCustomer = (cid) => {
    const c = customers.find((x) => x.id === cid);
    set("customer_id", cid);
    if (c) set("customer", { name: c.name, company: c.company, email: c.email, phone: c.phone, address: c.address });
  };

  const save = useCallback(async (thenPreview = false) => {
    setSaving(true);
    try {
      const payload = { ...f };
      let res;
      if (isNew) res = await api.post("/proposals", payload);
      else res = await api.put(`/proposals/${id}`, payload);
      toast.success("Proposal saved");
      const savedId = res.data.id;
      if (thenPreview) navigate(`/admin/proposals/${savedId}/preview`);
      else if (isNew) navigate(`/admin/proposals/${savedId}/edit`, { replace: true });
      else setF({ ...EMPTY, ...res.data });
    } catch (e) {
      toast.error("Failed to save proposal");
    } finally { setSaving(false); }
  }, [f, id, isNew, navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground"><Loader2 className="animate-spin mr-2" /> Loading…</div>;

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* sticky toolbar */}
      <div className="sticky top-0 z-40 bg-card/80 backdrop-blur-xl border-b border-border no-print">
        <div className="max-w-[1300px] mx-auto px-5 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/admin/proposals")} className="text-muted-foreground hover:text-foreground flex items-center gap-2 text-sm"><ArrowLeft size={16} /> Proposals</button>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground hidden sm:block">Total: <span className="font-mono-num font-semibold text-foreground">{money(total)}</span></span>
            <button onClick={() => save(false)} disabled={saving} data-testid="save-proposal" className="flex items-center gap-2 border border-border px-4 py-2.5 text-[11px] tracking-[0.1em] uppercase hover:bg-secondary transition-colors disabled:opacity-50">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</button>
            <button onClick={() => save(true)} disabled={saving} data-testid="save-preview" className="flex items-center gap-2 bg-foreground text-background px-4 py-2.5 text-[11px] tracking-[0.1em] uppercase hover:bg-onyx-wood hover:text-onyx-black transition-colors disabled:opacity-50"><Eye size={14} /> Save & Preview</button>
          </div>
        </div>
      </div>

      <div className="max-w-[1300px] mx-auto px-5 py-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card title="Proposal Details">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><label className={labelCls}>Title</label><input data-testid="field-title" className={input} value={f.title} onChange={(e) => set("title", e.target.value)} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Prepared By (person)</label><input data-testid="field-prepared-by" className={input} placeholder="e.g. Carlos Rosales" value={f.prepared_by} onChange={(e) => set("prepared_by", e.target.value)} /></div>
              <div><label className={labelCls}>Proposal Number</label><input className={input} placeholder="Auto-generated" value={f.proposal_number} onChange={(e) => set("proposal_number", e.target.value)} /></div>
              <div><label className={labelCls}>Status</label>
                <select className={input} value={f.status} onChange={(e) => set("status", e.target.value)}>
                  {["draft", "sent", "approved", "rejected"].map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div><label className={labelCls}>Date</label><input className={input} placeholder="Auto (today)" value={f.date} onChange={(e) => set("date", e.target.value)} /></div>
              <div><label className={labelCls}>Valid Until</label><input className={input} placeholder="Auto (+30 days)" value={f.expiration_date} onChange={(e) => set("expiration_date", e.target.value)} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm mt-4"><input type="checkbox" data-testid="toggle-signature" checked={f.show_signature} onChange={(e) => set("show_signature", e.target.checked)} /> Include signature / acceptance page</label>
          </Card>

          <Card title="Customer">
            <label className={labelCls}>Select existing customer</label>
            <select data-testid="field-customer" className={input} value={f.customer_id} onChange={(e) => selectCustomer(e.target.value)}>
              <option value="">— Enter manually below —</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` · ${c.company}` : ""}</option>)}
            </select>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              {["name", "company", "email", "phone"].map((k) => (
                <div key={k}><label className={labelCls}>{k}</label><input className={input} value={f.customer?.[k] || ""} onChange={(e) => set("customer", { ...f.customer, [k]: e.target.value })} /></div>
              ))}
            </div>
          </Card>

          <Card title="Project">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className={labelCls}>Project Name</label><input data-testid="field-project-name" className={input} value={f.project_name} onChange={(e) => set("project_name", e.target.value)} /></div>
              <div><label className={labelCls}>Project Address</label><input className={input} value={f.project_address} onChange={(e) => set("project_address", e.target.value)} /></div>
            </div>
            <div className="mt-4"><label className={labelCls}>Overview</label><textarea rows={4} className={`${input} resize-none`} value={f.overview} onChange={(e) => set("overview", e.target.value)} /></div>
          </Card>

          <div className="grid sm:grid-cols-2 gap-6">
            <Card title="Scope Includes"><StringList items={f.scope_includes} onChange={(v) => set("scope_includes", v)} placeholder="Scope item" testid="add-scope-inc" /></Card>
            <Card title="Exclusions"><StringList items={f.exclusions} onChange={(v) => set("exclusions", v)} placeholder="Exclusion" testid="add-exclusion" /></Card>
          </div>

          <Card title="Quantities" action={<button onClick={() => set("quantities", [...f.quantities, { item: "", qty: "" }])} data-testid="add-quantity" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5"><Plus size={14} /> Add row</button>}>
            <div className="space-y-2">
              {f.quantities.map((q, i) => (
                <div key={i} className="grid grid-cols-12 gap-2" data-testid={`quantity-${i}`}>
                  <input className={`${input} col-span-7`} placeholder="Item (e.g. Linear Feet)" value={q.item} onChange={(e) => set("quantities", f.quantities.map((x, idx) => idx === i ? { ...x, item: e.target.value } : x))} />
                  <input className={`${input} col-span-4`} placeholder="Qty (e.g. 425 LF)" value={q.qty} onChange={(e) => set("quantities", f.quantities.map((x, idx) => idx === i ? { ...x, qty: e.target.value } : x))} />
                  <button onClick={() => set("quantities", f.quantities.filter((_, idx) => idx !== i))} className="col-span-1 text-muted-foreground hover:text-destructive flex items-center justify-center"><Trash2 size={15} /></button>
                </div>
              ))}
              {f.quantities.length === 0 && <p className="text-sm text-muted-foreground">No quantities yet.</p>}
            </div>
          </Card>

          <Card title="Investment">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className={labelCls}>Labor / Installation ($)</label><input type="number" data-testid="field-labor" className={input} value={f.labor_total} onChange={(e) => set("labor_total", e.target.value)} /></div>
              <div><label className={labelCls}>Mobilizations Included</label><input className={input} value={f.mobilizations_included} onChange={(e) => set("mobilizations_included", e.target.value)} /></div>
              <div><label className={labelCls}>Estimated Duration</label><input className={input} placeholder="Up to 15 days" value={f.estimated_duration} onChange={(e) => set("estimated_duration", e.target.value)} /></div>
              <div><label className={labelCls}>Crew Size</label><input className={input} placeholder="3 workers" value={f.crew_size} onChange={(e) => set("crew_size", e.target.value)} /></div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">Proposal Total = Labor / Installation after discount &amp; tax.</p>
          </Card>

          <Card title="Our Work" action={<button onClick={() => set("our_work", [...f.our_work, { image: "", title: "", location: "", caption: "" }])} data-testid="add-ourwork" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5"><Plus size={14} /> Add item</button>}>
            <div className="space-y-4">
              {f.our_work.map((w, i) => (
                <div key={i} className="grid grid-cols-12 gap-3 border-b border-border pb-4" data-testid={`ourwork-${i}`}>
                  <div className="col-span-3"><ImageUploader value={w.image} onChange={(v) => set("our_work", f.our_work.map((x, idx) => idx === i ? { ...x, image: v } : x))} height="h-20" /></div>
                  <div className="col-span-8 space-y-2">
                    <input className={input} placeholder="Title" value={w.title} onChange={(e) => set("our_work", f.our_work.map((x, idx) => idx === i ? { ...x, title: e.target.value } : x))} />
                    <input className={input} placeholder="Location" value={w.location} onChange={(e) => set("our_work", f.our_work.map((x, idx) => idx === i ? { ...x, location: e.target.value } : x))} />
                    <input className={input} placeholder="Caption" value={w.caption} onChange={(e) => set("our_work", f.our_work.map((x, idx) => idx === i ? { ...x, caption: e.target.value } : x))} />
                  </div>
                  <button onClick={() => set("our_work", f.our_work.filter((_, idx) => idx !== i))} className="col-span-1 text-muted-foreground hover:text-destructive flex items-start justify-center pt-1"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Terms & Conditions Items" action={<button onClick={() => set("terms_items", [...f.terms_items, { title: "", body: "" }])} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5"><Plus size={14} /> Add</button>}>
            <div className="space-y-3">
              {f.terms_items.map((t, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <input className={`${input} col-span-4`} placeholder="Title" value={t.title} onChange={(e) => set("terms_items", f.terms_items.map((x, idx) => idx === i ? { ...x, title: e.target.value } : x))} />
                  <input className={`${input} col-span-7`} placeholder="Body" value={t.body} onChange={(e) => set("terms_items", f.terms_items.map((x, idx) => idx === i ? { ...x, body: e.target.value } : x))} />
                  <button onClick={() => set("terms_items", f.terms_items.filter((_, idx) => idx !== i))} className="col-span-1 text-muted-foreground hover:text-destructive flex items-center justify-center"><Trash2 size={15} /></button>
                </div>
              ))}
              <p className="text-xs text-muted-foreground">Leave empty to use standard default terms.</p>
            </div>
          </Card>

          <div className="grid sm:grid-cols-2 gap-6">
            <Card title="Scope of Work"><StringList items={f.scope_of_work} onChange={(v) => set("scope_of_work", v)} placeholder="Scope item" testid="add-scope" /></Card>
            <Card title="Materials & Specs"><StringList items={f.materials_specs} onChange={(v) => set("materials_specs", v)} placeholder="Material / spec" testid="add-material" /></Card>
          </div>

          <Card title="Project Timeline" action={<button onClick={() => set("timeline", [...f.timeline, { phase: "", duration: "", description: "" }])} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5"><Plus size={14} /> Add phase</button>}>
            <div className="space-y-3">
              {f.timeline.map((t, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <input className={`${input} col-span-4`} placeholder="Phase" value={t.phase} onChange={(e) => set("timeline", f.timeline.map((x, idx) => idx === i ? { ...x, phase: e.target.value } : x))} />
                  <input className={`${input} col-span-3`} placeholder="Duration" value={t.duration} onChange={(e) => set("timeline", f.timeline.map((x, idx) => idx === i ? { ...x, duration: e.target.value } : x))} />
                  <input className={`${input} col-span-4`} placeholder="Description" value={t.description} onChange={(e) => set("timeline", f.timeline.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x))} />
                  <button onClick={() => set("timeline", f.timeline.filter((_, idx) => idx !== i))} className="col-span-1 text-muted-foreground hover:text-destructive flex items-center justify-center"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Payment Schedule" action={<button onClick={() => set("payment_schedule", [...f.payment_schedule, { label: "", percent: 0, note: "" }])} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5"><Plus size={14} /> Add milestone</button>}>
            <div className="space-y-3">
              {f.payment_schedule.map((m, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <input className={`${input} col-span-5`} placeholder="Milestone" value={m.label} onChange={(e) => set("payment_schedule", f.payment_schedule.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))} />
                  <input className={`${input} col-span-2`} type="number" placeholder="%" value={m.percent} onChange={(e) => set("payment_schedule", f.payment_schedule.map((x, idx) => idx === i ? { ...x, percent: e.target.value } : x))} />
                  <input className={`${input} col-span-4`} placeholder="Note" value={m.note} onChange={(e) => set("payment_schedule", f.payment_schedule.map((x, idx) => idx === i ? { ...x, note: e.target.value } : x))} />
                  <button onClick={() => set("payment_schedule", f.payment_schedule.filter((_, idx) => idx !== i))} className="col-span-1 text-muted-foreground hover:text-destructive flex items-center justify-center"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </Card>

          <div className="grid gap-6">
            <Card title="Warranty"><textarea rows={3} className={`${input} resize-none`} value={f.warranty} onChange={(e) => set("warranty", e.target.value)} /></Card>
            <Card title="Terms & Conditions"><textarea rows={4} className={`${input} resize-none`} value={f.terms} onChange={(e) => set("terms", e.target.value)} /></Card>
            <Card title="Notes"><textarea rows={2} className={`${input} resize-none`} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Card>
          </div>
        </div>

        {/* right column */}
        <div className="space-y-6">
          <div className="sticky top-20 space-y-6">
            <Card title="Investment Summary">
              <div className="space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Labor / Installation</span><span className="font-mono-num" data-testid="summary-labor">{money(base)}</span></div>
                <div className="pt-3 border-t border-border">
                  <div className="flex items-center justify-between mb-2"><span className="text-xs uppercase tracking-wide text-muted-foreground">Additional Costs</span><button onClick={() => set("additional_costs", [...f.additional_costs, { label: "", amount: 0 }])} data-testid="add-additional-cost" className="text-muted-foreground hover:text-foreground"><Plus size={14} /></button></div>
                  {f.additional_costs.map((c, i) => (
                    <div key={i} className="flex gap-2 mb-2">
                      <input className={`${input} text-xs`} placeholder="Label" value={c.label} onChange={(e) => set("additional_costs", f.additional_costs.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))} />
                      <input className={`${input} text-xs w-24`} type="number" value={c.amount} onChange={(e) => set("additional_costs", f.additional_costs.map((x, idx) => idx === i ? { ...x, amount: e.target.value } : x))} />
                      <button onClick={() => set("additional_costs", f.additional_costs.filter((_, idx) => idx !== i))} className="text-muted-foreground hover:text-destructive"><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-baseline pt-3 border-t-2 border-foreground">
                  <span className="font-display font-bold">Proposal Total</span>
                  <span className="font-display font-extrabold text-xl font-mono-num" data-testid="summary-total">{money(total)}</span>
                </div>
              </div>
            </Card>

            <Card title="Cover & Media">
              <ImageUploader value={f.cover_image} onChange={(v) => set("cover_image", v)} label="Cover Image" testid="upload-cover" className="mb-4" />
              <ImageUploader value={f.logo} onChange={(v) => set("logo", v)} label="Logo (optional)" height="h-24" testid="upload-logo" className="mb-4" />
              <GalleryUploader value={f.images} onChange={(v) => set("images", v)} label="Project Images" testid="upload-gallery" />
            </Card>

            <Card title="Before / After">
              <div className="grid grid-cols-2 gap-3">
                <ImageUploader value={f.before_image} onChange={(v) => set("before_image", v)} label="Before" height="h-28" testid="upload-before" />
                <ImageUploader value={f.after_image} onChange={(v) => set("after_image", v)} label="After" height="h-28" testid="upload-after" />
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
